import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, ChevronDown, Loader2, X } from 'lucide-react'

// Shared bits for the OpCo admin screens.

export const OPCO_CODE_RE = /^[a-z0-9][a-z0-9-]{0,31}$/

export function validateOpcoCode(code) {
  if (!code) return 'Code is required'
  if (!OPCO_CODE_RE.test(code)) {
    return 'Use 1-32 lowercase letters, digits or hyphens, starting with a letter or digit'
  }
  return null
}

export function errorMessage(err, fallback = 'Something went wrong') {
  const data = err?.response?.data
  if (typeof data === 'string' && data) return data
  return data?.error || data?.message || err?.message || fallback
}

// Responses may be a bare list or wrapped ({opcos:[...]}, {members:[...]}...).
export function asList(data, ...keys) {
  if (Array.isArray(data)) return data
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key]
  }
  if (Array.isArray(data?.items)) return data.items
  return []
}

export function Modal({ title, onClose, children, footer, labelledBy = 'opco-modal-title', wide = false }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={`relative w-full ${wide ? 'max-w-3xl' : 'max-w-md'} rounded-2xl bg-white dark:bg-gray-900 shadow-xl ring-1 ring-gray-200 dark:ring-gray-800`}
      >
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 px-5 py-4">
          <h2 id={labelledBy} className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 border-t border-gray-100 dark:border-gray-800 px-5 py-3">{footer}</div>
        )}
      </div>
    </div>,
    document.body,
  )
}

export function ConfirmDialog({ title, message, children, confirmLabel = 'Confirm', danger = false, busy = false, onConfirm, onCancel }) {
  return (
    <Modal
      title={title}
      onClose={busy ? undefined : onCancel}
      labelledBy="opco-confirm-title"
      footer={(
        <>
          <button type="button" onClick={onCancel} disabled={busy} className={secondaryBtn}>Cancel</button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={danger ? dangerBtn : primaryBtn}
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </button>
        </>
      )}
    >
      <div className="flex gap-3">
        {danger && <AlertTriangle className="h-5 w-5 flex-shrink-0 text-red-500" />}
        <div className="min-w-0 space-y-2 text-sm text-gray-600 dark:text-gray-300">
          {message && <p>{message}</p>}
          {children}
        </div>
      </div>
    </Modal>
  )
}

export function PanelState({ loading, error, onRetry, empty, emptyText, children }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading...
      </div>
    )
  }
  if (error) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-sm text-red-600 dark:text-red-400" role="alert">
        <span>{error}</span>
        {onRetry && (
          <button type="button" onClick={onRetry} className="text-xs font-semibold text-navy-700 dark:text-navy-300 hover:underline">
            Try again
          </button>
        )}
      </div>
    )
  }
  if (empty) {
    return <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">{emptyText}</p>
  }
  return children
}

export const primaryBtn = 'inline-flex items-center justify-center gap-2 h-9 px-4 rounded-lg bg-navy-800 text-white text-sm font-semibold shadow-sm ring-1 ring-navy-900/20 hover:bg-navy-900 disabled:opacity-50 disabled:cursor-not-allowed'
export const secondaryBtn = 'inline-flex items-center justify-center gap-2 h-9 px-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50'
export const dangerBtn = 'inline-flex items-center justify-center gap-2 h-9 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold shadow-sm hover:bg-red-700 disabled:opacity-50'
export const inputCls = 'w-full h-9 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-navy-500/40'
export const cardCls = 'rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm'

const SUMMARY_TONE = {
  ok: 'text-green-700 dark:text-green-400',
  bad: 'text-red-600 dark:text-red-400',
  muted: 'text-gray-500 dark:text-gray-400',
}

// One expandable row (closed by default) for setup that most OpCos never touch,
// e.g. ERP credentials. The header always shows a one-line summary, so the state
// is readable without opening it. The body stays mounted (just hidden) so
// secrets typed but not yet saved survive a collapse. Rows sit inside a card
// that supplies the border and dividers.
export function CollapsibleSection({ id, icon: Icon, title, summary, tone = 'muted', open, onToggle, ariaLabel, children }) {
  return (
    <section aria-label={ariaLabel || title}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={id}
        className="flex w-full items-center gap-3 px-5 py-3.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40"
      >
        {Icon && <Icon className="h-4 w-4 flex-shrink-0 text-gray-400" />}
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</span>
        <span className={`ml-auto min-w-0 truncate text-xs ${SUMMARY_TONE[tone] || SUMMARY_TONE.muted}`}>{summary}</span>
        <ChevronDown className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <div id={id} hidden={!open} className="border-t border-gray-100 dark:border-gray-800">
        {children}
      </div>
    </section>
  )
}
